/// <reference lib="dom"/>
import * as rxjs from "rxjs";
import { getLogger } from "@logtape/logtape";
import {
  type ActionHandlers,
  Dispatcher,
  type DispatcherOptions,
  FPS_60,
  type Message,
} from "@utils/dispatch";
import { ScheduelerInitState, timerWorkerBlob } from "@wbaudio/utils";
import { MicConstraints, MicErrorMessage, WbaNodeId } from "./wbactx.utils.tsx";

//#region dispatch
export type WbaCtxStatus = "uninit" | AudioContextState | "interrupted";
export type WbaMicStatus = "idle" | "requesting" | "granted" | "error";
export type WbaTransportStatus = "stopped" | "playing" | "paused";
export type WbaWorkletStatus = "loading" | "loaded" | "error";

export interface WbaDevice {
  deviceId: string;
  groupId: string;
  label: string;
}

export interface WbaNodeMeta {
  id: string;
  kind: string;
  name: string;
  inputs: number;
  outputs: number;
  params: string[];
  createdAt: number;
}

export interface WbaEdge {
  id: string;
  from: string;
  to: string;
  output: number;
  input: number | null;
  param: string | null;
}

export interface WbaEdgeQuery {
  from: string;
  to?: string;
  output?: number;
  input?: number;
  param?: string | null;
}

export interface WbaStep {
  step: number;
  time: number;
  duration: number;
  sub: number;
  beat: number;
  bar: number;
  tempo: number;
}

export interface WbaCtxCoreState {
  "ctx/status": WbaCtxStatus;
  "ctx/sampleRate": number | null;
  "ctx/baseLatency": number | null;
  "ctx/outputLatency": number | null;
  "ctx/error": string | null;
  "master/gain": number;
}

export interface WbaMicState {
  "mic/status": WbaMicStatus;
  "mic/deviceId": string | null;
  "mic/devices": WbaDevice[];
  "mic/error": string | null;
}

export interface WbaTransportState {
  "transport/status": WbaTransportStatus;
  "transport/tempo": number;
  "transport/stepsPerBeat": number;
  "transport/beatsPerBar": number;
  "transport/lookahead": number;
  "transport/scheduleAhead": number;
  "transport/step": number;
  "transport/startedAt": number | null;
}

export interface WbaWorkletState {
  "worklets": Record<string, WbaWorkletStatus>;
}

export interface WbaGraphState {
  "graph/nodes": Record<string, WbaNodeMeta>;
  "graph/edges": Record<string, WbaEdge>;
}

export interface WbaCtxState
  extends WbaCtxCoreState, WbaMicState, WbaTransportState, WbaWorkletState, WbaGraphState {}

export type WbaTransportConfig = Partial<
  Pick<
    WbaTransportState,
    | "transport/tempo"
    | "transport/stepsPerBeat"
    | "transport/beatsPerBar"
    | "transport/lookahead"
    | "transport/scheduleAhead"
  >
>;

export const WbaCtxCoreDefaultState: WbaCtxCoreState = {
  "ctx/status": "uninit",
  "ctx/sampleRate": null,
  "ctx/baseLatency": null,
  "ctx/outputLatency": null,
  "ctx/error": null,
  "master/gain": 1,
};

export const WbaMicDefaultState: WbaMicState = {
  "mic/status": "idle",
  "mic/deviceId": null,
  "mic/devices": [],
  "mic/error": null,
};

export const WbaTransportDefaultState: WbaTransportState = {
  "transport/status": "stopped",
  "transport/tempo": ScheduelerInitState.tempo,
  "transport/stepsPerBeat": 4,
  "transport/beatsPerBar": 4,
  "transport/lookahead": ScheduelerInitState.lookahead,
  "transport/scheduleAhead": ScheduelerInitState.scheduleAheadTime,
  "transport/step": 0,
  "transport/startedAt": null,
};

export const WbaWorkletDefaultState: WbaWorkletState = {
  "worklets": {},
};

export const WbaGraphDefaultState: WbaGraphState = {
  "graph/nodes": {},
  "graph/edges": {},
};

export const WbaCtxDefaultState: WbaCtxState = {
  ...WbaCtxCoreDefaultState,
  ...WbaMicDefaultState,
  ...WbaTransportDefaultState,
  ...WbaWorkletDefaultState,
  ...WbaGraphDefaultState,
};

export interface WbaCtxActions {
  "ctx/init": undefined;
  "ctx/created": {
    status: WbaCtxStatus;
    sampleRate: number;
    baseLatency: number | null;
    outputLatency: number | null;
  };
  "ctx/resume": undefined;
  "ctx/suspend": undefined;
  "ctx/close": undefined;
  "ctx/statechange": { status: WbaCtxStatus; outputLatency: number | null };
  "ctx/error": { message: string };
  "master/gain": { value: number };
  "mic/request": { deviceId: string | null };
  "mic/granted": { deviceId: string | null; devices: WbaDevice[] };
  "mic/devices": { devices: WbaDevice[] };
  "mic/error": { message: string };
  "mic/release": undefined;
  "transport/play": undefined;
  "transport/started": { at: number; step: number };
  "transport/pause": undefined;
  "transport/stop": undefined;
  "transport/seek": { step: number };
  "transport/tempo": { tempo: number };
  "transport/config": WbaTransportConfig;
  "worklet/add": { url: string };
  "worklet/loaded": { url: string };
  "worklet/error": { url: string; message: string };
  "graph/node/add": WbaNodeMeta;
  "graph/node/remove": { id: string };
  "graph/connect": WbaEdge;
  "graph/disconnect": WbaEdgeQuery;
}

export type WbaCtxMessage = Message<WbaCtxActions>;
type Handlers = ActionHandlers<WbaCtxActions, WbaCtxState>;
//#endregion

//#region utils
export interface WbaCtxProps {
  ctx?: AudioContext;
  contextOptions?: AudioContextOptions;
  trackGraph?: boolean;
  transport?: WbaTransportConfig;
  dispatcher?: DispatcherOptions;
}

export interface WbaNodeProps {
  id?: string;
  name?: string;
}

export interface WbaMic {
  stream: MediaStream;
  node: MediaStreamAudioSourceNode;
}

const MainOutputId = "/main/output";
const MainMasterId = "/main/master";
const MainAnalyserId = "/main/analyser";
const MainMicId = "/main/mic";
const TransportStartDelay = 0.05;

const WbaCreateMethods = [
  "createAnalyser",
  "createBiquadFilter",
  "createBufferSource",
  "createChannelMerger",
  "createChannelSplitter",
  "createConstantSource",
  "createConvolver",
  "createDelay",
  "createDynamicsCompressor",
  "createGain",
  "createIIRFilter",
  "createMediaElementSource",
  "createMediaStreamDestination",
  "createMediaStreamSource",
  "createMediaStreamTrackSource",
  "createOscillator",
  "createPanner",
  "createScriptProcessor",
  "createStereoPanner",
  "createWaveShaper",
] as const;

export function WbaMic$(wbactx: WbaCtx): rxjs.Observable<AudioNode | null> {
  return rxjs.combineLatest([
    wbactx.select$((s) => s["mic/status"]),
    wbactx.select$((s) => s["mic/deviceId"]),
  ]).pipe(rxjs.map(() => wbactx.mic?.node ?? null));
}

export function StepDuration(tempo: number, stepsPerBeat: number) {
  return 60 / tempo / stepsPerBeat;
}

function ErrorMessage(e: unknown) {
  return e instanceof Error ? e.message : String(e);
}

function NodeKind(node: AudioNode) {
  return node.constructor.name.replace(/Node$/, "") || "AudioNode";
}

function NodeParams(node: AudioNode): [string, AudioParam][] {
  const params: [string, AudioParam][] = [];
  for (const key in node) {
    const value = (node as unknown as Record<string, unknown>)[key];
    if (value instanceof AudioParam) params.push([key, value]);
  }
  if (typeof AudioWorkletNode !== "undefined" && node instanceof AudioWorkletNode) {
    node.parameters.forEach((param, name) => params.push([name, param]));
  }
  return params;
}

function EdgeMatches(edge: WbaEdge, query: WbaEdgeQuery) {
  return edge.from === query.from &&
    (query.to === undefined || edge.to === query.to) &&
    (query.output === undefined || edge.output === query.output) &&
    (query.input === undefined || edge.input === query.input) &&
    (query.param === undefined || edge.param === query.param);
}

function WorkerPulse$(interval: number): rxjs.Observable<void> {
  if (typeof Worker !== "function") {
    return rxjs.interval(interval).pipe(rxjs.map(() => undefined));
  }
  return new rxjs.Observable<void>((subscriber) => {
    const url = URL.createObjectURL(timerWorkerBlob);
    const worker = new Worker(url);
    const onMessage = (e: MessageEvent) => {
      if (e.data === "tick") subscriber.next();
    };
    worker.addEventListener("message", onMessage);
    worker.postMessage({ interval });
    worker.postMessage("start");
    return () => {
      worker.removeEventListener("message", onMessage);
      worker.postMessage("stop");
      worker.terminate();
      URL.revokeObjectURL(url);
    };
  });
}

async function ListInputDevices(): Promise<WbaDevice[]> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices
    .filter((d) => d.kind === "audioinput")
    .map((d) => ({ deviceId: d.deviceId, groupId: d.groupId, label: d.label }));
}
//#endregion

export class WbaCtx extends Dispatcher<WbaCtxActions, WbaCtxState, WbaCtxProps> {
  protected readonly handlers: Handlers = {
    "ctx/init": ({ state }) => {
      this.#ensureContext();
      return state;
    },
    "ctx/created": ({ state, payload }) => ({
      ...state,
      "ctx/status": payload.status,
      "ctx/sampleRate": payload.sampleRate,
      "ctx/baseLatency": payload.baseLatency,
      "ctx/outputLatency": payload.outputLatency,
      "ctx/error": null,
    }),
    "ctx/resume": ({ state }) => {
      this.#ensureContext().resume().catch((e) =>
        this.dispatch({ type: "ctx/error", payload: { message: ErrorMessage(e) } })
      );
      return state;
    },
    "ctx/suspend": ({ state }) => {
      this.#ctx?.suspend().catch((e) =>
        this.dispatch({ type: "ctx/error", payload: { message: ErrorMessage(e) } })
      );
      return state;
    },
    "ctx/close": ({ state }) => {
      this.#closeContext(true);
      return {
        ...state,
        "ctx/status": "closed",
        "mic/status": "idle",
        "mic/deviceId": null,
        "transport/status": "stopped",
        "transport/step": 0,
        "transport/startedAt": null,
        "worklets": {},
        "graph/nodes": {},
        "graph/edges": {},
      };
    },
    "ctx/statechange": ({ state, payload }) => ({
      ...state,
      "ctx/status": payload.status,
      "ctx/outputLatency": payload.outputLatency,
    }),
    "ctx/error": ({ state, payload }) => ({ ...state, "ctx/error": payload.message }),

    "master/gain": ({ state, payload }) => {
      if (this.#master && this.#ctx) {
        this.#master.gain.setTargetAtTime(payload.value, this.#ctx.currentTime, 0.01);
      }
      return { ...state, "master/gain": payload.value };
    },

    "mic/request": ({ state, payload }) => {
      this.#requestMic(payload.deviceId);
      return { ...state, "mic/status": "requesting", "mic/error": null };
    },
    "mic/granted": ({ state, payload }) => ({
      ...state,
      "mic/status": "granted",
      "mic/deviceId": payload.deviceId,
      "mic/devices": payload.devices,
      "mic/error": null,
    }),
    "mic/devices": ({ state, payload }) => ({ ...state, "mic/devices": payload.devices }),
    "mic/error": ({ state, payload }) => ({
      ...state,
      "mic/status": "error",
      "mic/error": payload.message,
    }),
    "mic/release": ({ state }) => {
      this.#stopMic();
      return { ...state, "mic/status": "idle", "mic/deviceId": null };
    },

    "transport/play": ({ state }) => {
      if (state["transport/status"] === "playing") return state;
      this.#startTransport(state["transport/step"]);
      return { ...state, "transport/status": "playing" };
    },
    "transport/started": ({ state, payload }) => ({
      ...state,
      "transport/startedAt": payload.at,
      "transport/step": payload.step,
    }),
    "transport/pause": ({ state }) => {
      if (state["transport/status"] !== "playing") return state;
      this.#stopTransport();
      return { ...state, "transport/status": "paused", "transport/step": this.#step };
    },
    "transport/stop": ({ state }) => {
      this.#stopTransport();
      this.#step = 0;
      return {
        ...state,
        "transport/status": "stopped",
        "transport/step": 0,
        "transport/startedAt": null,
      };
    },
    "transport/seek": ({ state, payload }) => {
      this.#step = Math.max(0, Math.floor(payload.step));
      return { ...state, "transport/step": this.#step };
    },
    "transport/tempo": ({ state, payload }) => {
      if (!Number.isFinite(payload.tempo) || payload.tempo <= 0) return state;
      return { ...state, "transport/tempo": payload.tempo };
    },
    "transport/config": ({ state, payload }) => ({ ...state, ...payload }),

    "worklet/add": ({ state, payload }) => {
      if (state.worklets[payload.url]) return state;
      this.#loadWorklet(payload.url);
      return { ...state, worklets: { ...state.worklets, [payload.url]: "loading" } };
    },
    "worklet/loaded": ({ state, payload }) => ({
      ...state,
      worklets: { ...state.worklets, [payload.url]: "loaded" },
    }),
    "worklet/error": ({ state, payload }) => ({
      ...state,
      "ctx/error": payload.message,
      worklets: { ...state.worklets, [payload.url]: "error" },
    }),

    "graph/node/add": ({ state, payload }) => ({
      ...state,
      "graph/nodes": { ...state["graph/nodes"], [payload.id]: payload },
    }),
    "graph/node/remove": ({ state, payload }) => {
      const nodes = { ...state["graph/nodes"] };
      delete nodes[payload.id];
      const edges = Object.fromEntries(
        Object.entries(state["graph/edges"]).filter(([, e]) =>
          e.from !== payload.id && e.to !== payload.id
        ),
      );
      return { ...state, "graph/nodes": nodes, "graph/edges": edges };
    },
    "graph/connect": ({ state, payload }) => ({
      ...state,
      "graph/edges": { ...state["graph/edges"], [payload.id]: payload },
    }),
    "graph/disconnect": ({ state, payload }) => ({
      ...state,
      "graph/edges": Object.fromEntries(
        Object.entries(state["graph/edges"]).filter(([, e]) => !EdgeMatches(e, payload)),
      ),
    }),
  };

  #logger = getLogger(["webaudio", "wbactx"]);
  #ctx: AudioContext | null = null;
  #ownsCtx = false;
  #master: GainNode | null = null;
  #analyser: AnalyserNode | null = null;
  #mic: WbaMic | null = null;
  #watchingDevices = false;
  #worklets = new Map<string, Promise<void>>();

  #trackGraph: boolean;
  #pending: WbaNodeProps | null = null;
  #nodes = new Map<string, AudioNode>();
  #ids = new WeakMap<AudioNode, string>();
  #params = new WeakMap<AudioParam, { node: string; name: string }>();
  #idSeq = new Map<string, number>();

  #transportSub: rxjs.Subscription | null = null;
  #step = 0;
  #nextTime = 0;
  readonly #step$ = new rxjs.Subject<WbaStep>();

  readonly pulse$: rxjs.Observable<number>;
  readonly step$: rxjs.Observable<WbaStep> = this.#step$.asObservable();
  readonly audible$: rxjs.Observable<WbaStep>;
  readonly playhead$: rxjs.Observable<number>;

  constructor(props: WbaCtxProps = {}) {
    super(props, { ...WbaCtxDefaultState, ...props.transport }, {
      clock: { interval: FPS_60, now: () => performance.now() },
      ...props.dispatcher,
    });
    this.#trackGraph = props.trackGraph ?? true;

    this.pulse$ = this.select$((s) => s["transport/lookahead"]).pipe(
      rxjs.switchMap((interval) => WorkerPulse$(interval)),
      rxjs.map(() => this.#ctx?.currentTime ?? 0),
      rxjs.share(),
    );

    const halted$ = this.ofType$("transport/stop", "transport/pause", "ctx/close");
    this.audible$ = this.step$.pipe(
      rxjs.mergeMap((step) =>
        rxjs.timer(Math.max(0, (step.time - (this.#ctx?.currentTime ?? 0)) * 1000)).pipe(
          rxjs.map(() => step),
          rxjs.takeUntil(halted$),
        )
      ),
      rxjs.share(),
    );

    this.playhead$ = this.tick$.pipe(
      rxjs.map(() => this.position()),
      rxjs.distinctUntilChanged(),
      rxjs.share(),
    );
  }

  //#region accessors
  get ctx(): AudioContext {
    return this.#ensureContext();
  }

  get now(): number {
    return this.#ctx?.currentTime ?? 0;
  }

  get output(): GainNode {
    this.#ensureContext();
    return this.#master!;
  }

  get analyser(): AnalyserNode {
    this.#ensureContext();
    return this.#analyser!;
  }

  get destination(): AudioDestinationNode {
    return this.#ensureContext().destination;
  }

  get mic(): WbaMic | null {
    return this.#mic;
  }

  get stepDuration(): number {
    return StepDuration(this.state["transport/tempo"], this.state["transport/stepsPerBeat"]);
  }

  position(time?: number): number {
    if (!this.#ctx || !this.#transportSub) return this.state["transport/step"];
    const at = time ?? this.#ctx.currentTime - (this.#ctx.outputLatency || 0);
    return Math.max(0, this.#step - (this.#nextTime - at) / this.stepDuration);
  }
  //#endregion

  //#region commands
  unlock(target: EventTarget = globalThis): rxjs.Subscription {
    return this.dispatchFrom(
      rxjs.merge(rxjs.fromEvent(target, "pointerdown"), rxjs.fromEvent(target, "keydown")).pipe(
        rxjs.take(1),
        rxjs.map((): WbaCtxMessage => ({ type: "ctx/resume" })),
      ),
    );
  }

  addWorklet(url: string): Promise<void> {
    const loading = this.#loadWorklet(url);
    this.dispatch({ type: "worklet/add", payload: { url } });
    return loading;
  }
  //#endregion

  //#region graph
  node<N extends AudioNode>(make: (ctx: AudioContext) => N, props: WbaNodeProps = {}): N {
    const ctx = this.#ensureContext();
    this.#pending = props;
    try {
      return this.track(make(ctx), props);
    } finally {
      this.#pending = null;
    }
  }

  track<N extends AudioNode>(node: N, props: WbaNodeProps = {}): N {
    if (!this.#trackGraph || this.#ids.has(node)) return node;

    const p = { ...this.#pending, ...props };
    this.#pending = null;

    const kind = NodeKind(node);
    const existing = (node as unknown as { id?: unknown }).id;
    const id = p.id ? this.#uniqueId(WbaNodeId(p.id), false) : this.#uniqueId(
      WbaNodeId(typeof existing === "string" ? existing : kind),
      typeof existing !== "string",
    );

    const params = NodeParams(node);
    for (const [name, param] of params) this.#params.set(param, { node: id, name });

    this.#ids.set(node, id);
    this.#nodes.set(id, node);
    this.#patchNode(node, id);

    if (
      typeof AudioScheduledSourceNode !== "undefined" && node instanceof AudioScheduledSourceNode
    ) {
      node.addEventListener("ended", () => this.untrack(node), { once: true });
    }

    this.dispatch({
      type: "graph/node/add",
      payload: {
        id,
        kind,
        name: p.name ?? "untitled",
        inputs: node.numberOfInputs,
        outputs: node.numberOfOutputs,
        params: params.map(([name]) => name),
        createdAt: node.context.currentTime,
      },
    });
    return node;
  }

  untrack(node: AudioNode) {
    const id = this.#ids.get(node);
    if (!id) return;
    this.#ids.delete(node);
    this.#nodes.delete(id);
    this.dispatch({ type: "graph/node/remove", payload: { id } });
  }

  release(node: AudioNode) {
    node.disconnect();
    this.untrack(node);
  }

  idOf(node: AudioNode): string | undefined {
    return this.#ids.get(node);
  }

  getNode<N extends AudioNode = AudioNode>(id: string): N | undefined {
    return this.#nodes.get(id) as N | undefined;
  }

  #uniqueId(base: string, numbered: boolean) {
    if (!numbered && !this.#nodes.has(base)) return base;
    let n = this.#idSeq.get(base) ?? 0;
    do n++; while (this.#nodes.has(`${base}-${n}`));
    this.#idSeq.set(base, n);
    return `${base}-${n}`;
  }

  #patchContext(ctx: AudioContext) {
    for (const method of WbaCreateMethods) {
      const original = (ctx as unknown as Record<string, unknown>)[method];
      if (typeof original !== "function") continue;
      Object.defineProperty(ctx, method, {
        configurable: true,
        writable: true,
        value: (...args: unknown[]) => {
          const node = original.apply(ctx, args);
          if (node instanceof AudioNode) this.track(node);
          return node;
        },
      });
    }
  }

  #patchNode(node: AudioNode, id: string) {
    const connect = node.connect as (...args: unknown[]) => unknown;
    const disconnect = node.disconnect as (...args: unknown[]) => unknown;
    Object.defineProperty(node, "connect", {
      configurable: true,
      writable: true,
      value: (...args: unknown[]) => {
        const result = connect.apply(node, args);
        this.#onConnect(id, args);
        return result;
      },
    });
    Object.defineProperty(node, "disconnect", {
      configurable: true,
      writable: true,
      value: (...args: unknown[]) => {
        const result = disconnect.apply(node, args);
        this.#onDisconnect(id, args);
        return result;
      },
    });
  }

  #onConnect(from: string, [dest, output, input]: unknown[]) {
    if (!this.#nodes.has(from)) return;
    const out = typeof output === "number" ? output : 0;

    if (dest instanceof AudioParam) {
      const owner = this.#params.get(dest);
      if (!owner) return;
      this.dispatch({
        type: "graph/connect",
        payload: {
          id: `${from}:${out}->${owner.node}#${owner.name}`,
          from,
          to: owner.node,
          output: out,
          input: null,
          param: owner.name,
        },
      });
    } else if (dest instanceof AudioNode) {
      const to = this.#ids.get(dest) ?? this.#ids.get(this.track(dest));
      if (!to) return;
      const inp = typeof input === "number" ? input : 0;
      this.dispatch({
        type: "graph/connect",
        payload: {
          id: `${from}:${out}->${to}:${inp}`,
          from,
          to,
          output: out,
          input: inp,
          param: null,
        },
      });
    }
  }

  #onDisconnect(from: string, [dest, output, input]: unknown[]) {
    if (!this.#nodes.has(from)) return;

    if (dest === undefined) {
      this.dispatch({ type: "graph/disconnect", payload: { from } });
    } else if (typeof dest === "number") {
      this.dispatch({ type: "graph/disconnect", payload: { from, output: dest } });
    } else if (dest instanceof AudioParam) {
      const owner = this.#params.get(dest);
      if (!owner) return;
      this.dispatch({
        type: "graph/disconnect",
        payload: {
          from,
          to: owner.node,
          param: owner.name,
          output: typeof output === "number" ? output : undefined,
        },
      });
    } else if (dest instanceof AudioNode) {
      const to = this.#ids.get(dest);
      if (!to) return;
      this.dispatch({
        type: "graph/disconnect",
        payload: {
          from,
          to,
          param: null,
          output: typeof output === "number" ? output : undefined,
          input: typeof input === "number" ? input : undefined,
        },
      });
    }
  }
  //#endregion

  //#region context
  #onStateChange = () => {
    if (!this.#ctx) return;
    this.dispatch({
      type: "ctx/statechange",
      payload: {
        status: this.#ctx.state as WbaCtxStatus,
        outputLatency: this.#ctx.outputLatency ?? null,
      },
    });
  };

  #ensureContext(): AudioContext {
    if (this.#ctx && this.#ctx.state !== "closed") return this.#ctx;

    const given = this.props.ctx;
    const ctx = given && given.state !== "closed"
      ? given
      : new AudioContext(this.props.contextOptions);
    this.#logger.info`create context`;

    this.#ctx = ctx;
    this.#ownsCtx = ctx !== given;
    if (this.#trackGraph) this.#patchContext(ctx);
    ctx.addEventListener("statechange", this.#onStateChange);

    this.track(ctx.destination, { id: MainOutputId, name: "destination" });
    this.#master = this.node((c) => c.createGain(), { id: MainMasterId, name: "master" });
    this.#master.gain.value = this.state["master/gain"];
    this.#analyser = this.node((c) => c.createAnalyser(), { id: MainAnalyserId, name: "analyser" });
    this.#master.connect(ctx.destination);
    this.#master.connect(this.#analyser);

    this.dispatch({
      type: "ctx/created",
      payload: {
        status: ctx.state as WbaCtxStatus,
        sampleRate: ctx.sampleRate,
        baseLatency: ctx.baseLatency ?? null,
        outputLatency: ctx.outputLatency ?? null,
      },
    });
    return ctx;
  }

  #closeContext(force: boolean) {
    this.#stopTransport();
    this.#step = 0;
    this.#stopMic();

    const ctx = this.#ctx;
    if (!ctx) return;
    ctx.removeEventListener("statechange", this.#onStateChange);
    this.#ctx = null;
    this.#master = null;
    this.#analyser = null;
    this.#worklets.clear();
    this.#nodes.clear();
    this.#ids = new WeakMap();
    this.#idSeq.clear();
    if ((force || this.#ownsCtx) && ctx.state !== "closed") {
      ctx.close().catch((e) =>
        this.dispatch({ type: "ctx/error", payload: { message: ErrorMessage(e) } })
      );
    }
  }

  #loadWorklet(url: string): Promise<void> {
    const existing = this.#worklets.get(url);
    if (existing) return existing;

    const loading = this.#ensureContext().audioWorklet.addModule(url).then(
      () => {
        this.dispatch({ type: "worklet/loaded", payload: { url } });
      },
      (e) => {
        this.#worklets.delete(url);
        this.dispatch({ type: "worklet/error", payload: { url, message: ErrorMessage(e) } });
        throw e;
      },
    );
    this.#worklets.set(url, loading);
    return loading;
  }
  //#endregion

  //#region mic
  async #requestMic(deviceId: string | null) {
    this.#ensureContext();

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: deviceId ? { ...MicConstraints, deviceId: { exact: deviceId } } : MicConstraints,
      });
    } catch (e) {
      this.dispatch({ type: "mic/error", payload: { message: MicErrorMessage(e) } });
      return;
    }

    this.#stopMic();
    const node = this.node((c) => c.createMediaStreamSource(stream), {
      id: MainMicId,
      name: "mic",
    });
    this.#mic = { stream, node };
    this.#watchDevices();

    let devices: WbaDevice[] = [];
    try {
      devices = await ListInputDevices();
    } catch (e) {
      this.#logger.warn`enumerate devices failed: ${ErrorMessage(e)}`;
    }

    const actual = stream.getAudioTracks()[0]?.getSettings().deviceId ?? deviceId;
    this.dispatch({ type: "mic/granted", payload: { deviceId: actual ?? null, devices } });
  }

  #stopMic() {
    if (!this.#mic) return;
    for (const track of this.#mic.stream.getTracks()) track.stop();
    this.release(this.#mic.node);
    this.#mic = null;
  }

  #watchDevices() {
    if (this.#watchingDevices) return;
    this.#watchingDevices = true;
    this.dispatchFrom(
      rxjs.fromEvent(navigator.mediaDevices, "devicechange").pipe(
        rxjs.switchMap(() => rxjs.from(ListInputDevices()).pipe(rxjs.catchError(() => rxjs.EMPTY))),
        rxjs.map((devices): WbaCtxMessage => ({ type: "mic/devices", payload: { devices } })),
      ),
    );
  }
  //#endregion

  //#region transport
  #startTransport(step: number) {
    const ctx = this.#ensureContext();
    if (ctx.state === "suspended") {
      ctx.resume().catch((e) =>
        this.dispatch({ type: "ctx/error", payload: { message: ErrorMessage(e) } })
      );
    }

    this.#stopTransport();
    this.#step = step;
    this.#nextTime = ctx.currentTime + TransportStartDelay;
    this.#transportSub = this.pulse$.subscribe(() => this.#schedule());
    this.dispatch({ type: "transport/started", payload: { at: this.#nextTime, step } });
    this.#schedule();
  }

  #stopTransport() {
    this.#transportSub?.unsubscribe();
    this.#transportSub = null;
  }

  #schedule() {
    const ctx = this.#ctx;
    if (!ctx || !this.#transportSub) return;

    const s = this.state;
    const tempo = s["transport/tempo"];
    const stepsPerBeat = s["transport/stepsPerBeat"];
    const stepsPerBar = stepsPerBeat * s["transport/beatsPerBar"];
    const duration = StepDuration(tempo, stepsPerBeat);

    if (this.#nextTime < ctx.currentTime - duration) {
      const missed = Math.ceil((ctx.currentTime - this.#nextTime) / duration);
      this.#step += missed;
      this.#nextTime += missed * duration;
    }

    const horizon = ctx.currentTime + s["transport/scheduleAhead"];
    while (this.#nextTime < horizon) {
      const step = this.#step;
      this.#step$.next({
        step,
        time: this.#nextTime,
        duration,
        sub: step % stepsPerBeat,
        beat: Math.floor(step / stepsPerBeat),
        bar: Math.floor(step / stepsPerBar),
        tempo,
      });
      this.#step++;
      this.#nextTime += duration;
    }
  }
  //#endregion

  override dispose() {
    if (this.disposed) return;
    this.#closeContext(false);
    this.#step$.complete();
    super.dispose();
  }
}

let _wbactx: WbaCtx | null = null;
export function GetWbaCtx(props?: WbaCtxProps): WbaCtx {
  if (!_wbactx || _wbactx.disposed) _wbactx = new WbaCtx(props);
  return _wbactx;
}
